import { QuestionsFormData } from '@/types/questions'
import { use, useEffect } from 'react'
import { RegistrationCheckContext } from '@/providers/registration-check-provider'
import { RegistrationDataContext } from '@/providers/registration-data-provider/registration-data-provider'
import { ActivityContext } from '@/components/registration-check/ActivityItemProvider'

const SINGLE_SELECT_PLACEHOLDER = '[INPUT:SingleSelect]'
const MULTI_SELECT_PLACEHOLDER = '[INPUT:MultiSelect]'

/**
 * Adds the given answer to the question text.
 * If the question is a single select or multi select, the answer is added to the text as a single select or multi select.
 * Otherwise, the answer is added to the end of the text. */
const addAnswerToQuestionText = (text: string, givenAnswer: string) => {
    if (!text?.length || !givenAnswer?.length) return ''
    if (text.includes(SINGLE_SELECT_PLACEHOLDER) || text.includes(MULTI_SELECT_PLACEHOLDER)) {
        return text
            .replace(SINGLE_SELECT_PLACEHOLDER, `["${givenAnswer}"]`)
            .replace(MULTI_SELECT_PLACEHOLDER, `["${givenAnswer}"]`)
    } else {
        return text + ' ' + `[${givenAnswer}]`
    }
}

/** Removes everything between {DEFAULT} and {/DEFAULT}, including the "default" keywords*/
const removeDefaultAnswer = (text: string) => {
    return text.replace(/\{DEFAULT\}[\s\S]*?\{\/DEFAULT\}/g, '')
}

/**
 * Hook that collects the answers for an activity and stores them in the registration check answers in the registration data context.
 */
export const useActivityAnswerCollecting = () => {
    const { questions } = use(RegistrationCheckContext)
    const { registrationCheckAnswers, setRegistrationCheckAnswers } = use(RegistrationDataContext)
    const activityContext = use(ActivityContext)

    if (!activityContext) return
    const { getQuestionIdFromFormDataKey, formData, visibleQuestions, activity } = activityContext

    /** Gets the answer for a single select question. */
    const getAnswerSingle = (key: string, value: string) => [
        questions
            .find((question) => question.id === getQuestionIdFromFormDataKey(key))
            ?.questionContent.answers.find((answer) => answer.id === value),
    ]

    /** Gets the answers for a multiple select question. */
    const getAnswerMultiple = (key: string, value: string[]) =>
        value.map(
            (v) =>
                questions
                    .find((question) => question.id === getQuestionIdFromFormDataKey(key))
                    ?.questionContent.answers.find((answer) => answer.id === v),
        )

    /**
     * Gets all answers the user has given for the activity and pairs them with the question text.
     */
    const getUserAnswers = (formData: QuestionsFormData): string[] => {
        return Object.entries(formData).map(([key, value]) => {
            const question = questions.find((q) => q.id === getQuestionIdFromFormDataKey(key))
            const questionText = question?.questionContent.text.nl

            const answer = Array.isArray(value) ? getAnswerMultiple(key, value) : getAnswerSingle(key, value)

            const answerText = answer.map((a) => (typeof a === 'string' ? a : a?.text.nl)).join(', ')
            return addAnswerToQuestionText(removeDefaultAnswer(questionText), answerText)
        })
    }

    useEffect(() => {
        const userAnswers = getUserAnswers(formData)
        if (!registrationCheckAnswers && !userAnswers.length) return
        if (registrationCheckAnswers?.length) {
            const registrationCheck = registrationCheckAnswers.filter((r) => r.activityId !== activity.id)
            setRegistrationCheckAnswers([...registrationCheck, { activityId: activity.id, answers: userAnswers }])
        } else {
            setRegistrationCheckAnswers([{ activityId: activity.id, answers: userAnswers }])
        }
    }, [visibleQuestions])

    return {}
}
